import { useState, useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

/**
 * Standalone accordion-item renderer.
 *
 * When accordion-item appears as a child of an accordion, the AccordionRenderer
 * handles it inline. This renderer handles the case where accordion-item appears
 * as a standalone element type (e.g., in blocks-v2 that use it outside accordion
 * or when the element tree is flat).
 */
export function AccordionItemRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [isOpen, setIsOpen] = useState(false)

  const handleToggle = useCallback(() => {
    const willOpen = !isOpen
    setIsOpen(willOpen)
    dispatchEvent(element.id, "toggle", { open: willOpen })
  }, [isOpen, element.id, dispatchEvent])

  const title = element.text
    ? String(resolveSymbol(element.text) ?? "")
    : "Section"

  return (
    <div className={cn("w-full border-b last:border-b-0", element.className)}>
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium transition-all hover:bg-muted/50"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={cn("shrink-0 transition-transform duration-200", isOpen && "rotate-180")}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-4 pb-3 pt-0 text-sm text-muted-foreground">
          {element.children?.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </div>
      )}
    </div>
  )
}
